import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { F0Dialog, StandardLayout } from '@factorialco/f0-react'
import { Tabs } from '@factorialco/f0-react/dist/experimental'
import {
  AttendanceCustomPeriodEmployeeItemFragment,
  AttendanceSidepanelCompensationBlockFragment,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { i18nDateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import CompensationModalWrapper from 'modules/attendance/components/ClockIn/TabsBreakdown/CompensationModalWrapper'
import {
  buildTimeTrackingDetailParamsFromParams,
  usePersistedTimeTrackingViewType,
} from 'modules/attendance/components/TimeTrackingCollection/lib/timeTrackingNavigation'
import { useAttendanceDateNavigator } from 'modules/attendance/lib/attendanceDateNavigatorContext'
import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'

import { CompensationTab } from './CompensationTab'
import { EmployeeHeader } from './EmployeeHeader'
import { OverviewTab } from './OverviewTab'
import type { TabId } from './types'
import { useEmployeeApproval } from './useEmployeeApproval'
import { useEmployeeStatus } from './useEmployeeStatus'
import { usePrimaryAction } from './usePrimaryAction'

interface Props {
  employee: AttendanceCustomPeriodEmployeeItemFragment | null
  onClose: () => void
  onEmployeeUpdate?: (updatedEmployee: AttendanceCustomPeriodEmployeeItemFragment) => void
}

interface SidepanelContentProps {
  employee: AttendanceCustomPeriodEmployeeItemFragment
  onClose: () => void
  onEmployeeUpdate?: (updatedEmployee: AttendanceCustomPeriodEmployeeItemFragment) => void
}

const SidepanelContent = ({ employee, onClose, onEmployeeUpdate }: SidepanelContentProps) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<TabId>('overview')
  const { featureAvailability: hasCompensationFlowV2 } = useAttendanceFeatures(
    'dev_time_tracking_compensation_flow_v2'
  )
  const [selectedCompensationBlocks, setSelectedCompensationBlocks] = useState<
    AttendanceSidepanelCompensationBlockFragment[]
  >([])
  const [wizardModalOpen, setWizardModalOpen] = useState(false)
  const [shouldOpenCompensationModalAfterApproval, setShouldOpenCompensationModalAfterApproval] =
    useState(false)

  const { cycle, dateFrom, dateTo } = useAttendanceDateNavigator()
  const routeParams = useParams()
  const persistViewType = usePersistedTimeTrackingViewType()

  const timeSheetDate = moment(dateFrom)

  useEffect(() => {
    if (shouldOpenCompensationModalAfterApproval && employee.reviewStatus === 'approved') {
      setShouldOpenCompensationModalAfterApproval(false)
      setWizardModalOpen(true)
    }
  }, [shouldOpenCompensationModalAfterApproval, employee.reviewStatus, employee.id])

  const { handleApprove, handleDisapprove } = useEmployeeApproval({
    employee,
    cycle,
    dateFrom,
    dateTo,
    onEmployeeUpdate,
  })

  const status = useEmployeeStatus(employee)
  const primaryAction = usePrimaryAction({
    employee,
    approvePeriod: handleApprove,
    disapprovePeriod: handleDisapprove,
    activeTab,
    selectedCompensationBlocks,
    openCompensationModal: () => setWizardModalOpen(true),
  })

  const { featureAvailability: hasHourBalance } = useAttendanceFeatures('dev_hour_balance_ui')
  const { featureAvailability: canSeeHourBalance } =
    useAttendanceFeatures('hour_balance_visibility')
  const showHourBalance = hasHourBalance && canSeeHourBalance

  const handleOpenTimesheet = () => {
    track(trackingEvents.timeTracking.goToEmployeeTimeSheetFromTTSidepanel, {
      period_type: cycle,
      start_on: i18nDateToString(dateFrom, 'backend'),
      end_on: i18nDateToString(dateTo, 'backend'),
    })

    const detailParams = buildTimeTrackingDetailParamsFromParams(routeParams, dateFrom, cycle)
    persistViewType(detailParams)
    navigate(
      navigation.timeTracking.employee.periodTypeYearMonthDayEmployeeId.fullPath({
        ...detailParams,
        employeeId: employee.id ?? '',
      })
    )
  }
  const isDaily = Boolean(employee.referenceContract?.frForfaitJours)

  return (
    <>
      <F0Dialog
        isOpen
        onClose={wizardModalOpen ? () => {} : onClose}
        position="right"
        title={i18n.t('time_tracking.table_collection.employee_summary.title')}
        primaryAction={
          primaryAction
            ? {
                label: primaryAction.label,
                onClick: primaryAction.onClick,
                icon: primaryAction.icon,
              }
            : undefined
        }
      >
        <StandardLayout>
          <EmployeeHeader
            employee={employee}
            status={status}
            isDaily={isDaily}
            onOpenTimesheet={handleOpenTimesheet}
          />
          {hasCompensationFlowV2 ? (
            <>
              <Tabs
                activeTabId={activeTab}
                tabs={
                  [
                    {
                      index: true,
                      id: 'overview',
                      label: i18n.t(
                        'time_tracking.table_collection.employee_summary.tabs.overview'
                      ),
                      onClick: () => setActiveTab('overview'),
                    },
                    ...(showHourBalance
                      ? [
                          {
                            id: 'compensation' as const,
                            label: i18n.t(
                              'time_tracking.table_collection.employee_summary.tabs.compensation'
                            ),
                            onClick: () => setActiveTab('compensation'),
                          },
                        ]
                      : []),
                  ] as const
                }
              />
              {activeTab === 'overview' && <OverviewTab employee={employee} />}
              {activeTab === 'compensation' && showHourBalance && employee.id && (
                <CompensationTab
                  employee={employee}
                  setSelectedCompensationBlocks={setSelectedCompensationBlocks}
                />
              )}
            </>
          ) : (
            <OverviewTab employee={employee} />
          )}
        </StandardLayout>
      </F0Dialog>
      {hasCompensationFlowV2 && showHourBalance && employee.id && (
        <CompensationModalWrapper
          employeeId={Number(employee.id)}
          timeSheetDate={timeSheetDate}
          openModal={wizardModalOpen}
          setOpenModal={setWizardModalOpen}
          skipQuery
          onClose={() => {
            setSelectedCompensationBlocks([])
            onClose()
          }}
          selectedCompensationBlocks={selectedCompensationBlocks}
        />
      )}
    </>
  )
}

const CustomPeriodEmployeeSidepanel = ({ employee, onClose, onEmployeeUpdate }: Props) => {
  if (!employee) return null

  return (
    <SidepanelContent employee={employee} onClose={onClose} onEmployeeUpdate={onEmployeeUpdate} />
  )
}

export default CustomPeriodEmployeeSidepanel
